import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, CreditCard, Mail, Wallet } from "lucide-react";
import Pagination from "../Pagination";
import Loader from "../../loader/Loader";
import { domain } from "../../../security";
import CustomerCreditModal from "./CustomerCreditModal";

// --- CREDIT BADGE (FIXED DECIMALS) ---
export const CreditBadge = ({ customerId, refreshTrigger }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchBalance = async () => {
      try {
        const res = await axios.get(
          `${domain}/api/CustomerCreditLimits/${customerId}`,
        );
        if (!cancelled) setBalance(res.data.availableBalance);
      } catch {
        if (!cancelled) setBalance(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBalance();
    return () => {
      cancelled = true;
    };
  }, [customerId, refreshTrigger]); // <-- add refreshTrigger dependency

  if (loading) return null;
  if (balance === null) return null;

  // ✅ Use proper currency formatting with two decimals (no compact)
  const formatted = new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(balance);

  return (
    <span
      className={`text-xs font-bold px-2 py-1 rounded-full ${
        balance < 0
          ? "bg-red-100 text-red-600"
          : balance === 0
            ? "bg-slate-100 text-slate-500"
            : "bg-emerald-100 text-emerald-700"
      }`}
    >
      {formatted}
    </span>
  );
};

const AllCustomerCredit = () => {
  const [customerData, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creditCustomer, setCreditCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0); // used to trigger badge re-fetch
  const itemsPerPage = 10;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${domain}/api/Customers`);
      setCustomerData(response.data);
    } catch {
      console.error("Failed to fetch customers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Search filter
  const filteredCustomers = customerData.filter((item) => {
    const lower = searchQuery.toLowerCase();
    return (
      (item.customerName || "").toLowerCase().includes(lower) ||
      (item.email || "").toLowerCase().includes(lower) ||
      (item.customerCode || "").toLowerCase().includes(lower)
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  // Handle credit update (call after any successful transaction)
  const handleCreditUpdate = () => {
    // Force all badges to re-fetch by incrementing refreshKey
    setRefreshKey((prev) => prev + 1);
    // Also optionally refresh the whole customer list (if needed)
    fetchData();
  };

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <CreditCard size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Customer Credit Ledger
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Manage, add, and monitor credit balances for all customers.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Search */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search Customer Name, Code, or Email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-4 py-2 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader />
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full text-sm text-left text-slate-600 hidden md:table">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 font-semibold">Customer Details</th>
                  <th className="px-6 py-4 font-semibold">Contact Info</th>
                  <th className="px-6 py-4 font-semibold text-center">
                    Credit Balance
                  </th>
                  <th className="px-6 py-4 font-semibold text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {currentCustomers.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50 transition">
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-800">
                        {item.customerName}
                      </div>
                      <div className="text-[10px] font-mono text-slate-400">
                        {item.customerCode}
                      </div>
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded mt-1 inline-block text-slate-500">
                        {item.customerType || "Walk-In"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Mail size={14} className="text-slate-400" />{" "}
                        {item.email || "No Email"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center align-middle">
                      <CreditBadge
                        customerId={item.id}
                        refreshTrigger={refreshKey} // pass refresh key
                      />
                    </td>
                    <td className="px-6 py-4 text-right align-middle">
                      <button
                        onClick={() => setCreditCustomer(item)}
                        className="inline-flex items-center justify-center gap-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-100 px-4 py-2 rounded-lg text-xs font-semibold transition"
                      >
                        <Wallet size={16} /> Manage Credit
                      </button>
                    </td>
                  </tr>
                ))}
                {currentCustomers.length === 0 && (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-10 text-center text-slate-400"
                    >
                      No customers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Mobile View */}
            <div className="md:hidden flex flex-col divide-y divide-slate-100">
              {currentCustomers.map((item) => (
                <div
                  key={item.id}
                  className="p-4 flex flex-col gap-3 hover:bg-slate-50"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-slate-800">
                        {item.customerName}
                      </div>
                      <div className="text-[10px] font-mono text-slate-400">
                        {item.customerCode}
                      </div>
                    </div>
                    <CreditBadge
                      customerId={item.id}
                      refreshTrigger={refreshKey}
                    />
                  </div>
                  <button
                    onClick={() => setCreditCustomer(item)}
                    className="w-full py-2 bg-emerald-50 text-emerald-600 rounded-lg text-sm font-semibold flex justify-center items-center gap-2"
                  >
                    <Wallet size={16} /> Manage Credit Ledger
                  </button>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-slate-100">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredCustomers.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>
          </div>
        )}
      </div>

      {creditCustomer && (
        <CustomerCreditModal
          customer={creditCustomer}
          onClose={() => setCreditCustomer(null)}
          onUpdate={handleCreditUpdate} // ✅ pass the callback
        />
      )}
    </div>
  );
};

export default AllCustomerCredit;
